import {
    Box,
    Button,
    capitalize,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {Aufgabe, Prompt, PromptListe} from "../../api";
import {useAufgabe} from "../../client/queries";
import {usePromptsByAufgabeId, usePromptsByAufgabeInbound} from "../../client/queries";
import {useAufgabenHandling} from "../../hooks/aufgaben/useAufgabenHandling";
import {useAufgabenPromptHandling} from "../../hooks/aufgaben/useAufgabenPromptHandling";
import {useAufgabenInboundPromptHandling} from "../../hooks/aufgaben/useAufgabenInboundPromptHandling";
import AufgabePromptVersionEditor, {versionLabel} from "../../components/aufgaben/AufgabePromptVersionEditor";
import AufgabeInboundPromptVersionEditor from "../../components/aufgaben/AufgabeInboundPromptVersionEditor";
import {PromptModeToggle, type PromptMode} from "../../components/aufgaben/AufgabePromptVersionsSwitcher";
import DialogCreatePromptVersion from "../../components/aufgaben/DialogCreatePromptVersion";
import LocalDate from "../../components/LocalDate";
import ErrorAlert from "../../components/ErrorAlert";
import MultipleGuard from "../../components/guards/MultipleGuard";
import {permissions} from "../../permissions";
import {notPermitted} from "../../errors";

export default function PageAufgabeVersions() {
    const {aufgabeId = ""} = useParams<{aufgabeId: string}>();
    const {t: translation} = useTranslation('aufgaben');
    const {canEditAufgabeField} = useAufgabenHandling();
    const aufgabe: Aufgabe = useAufgabe(aufgabeId);
    const [mode, setMode] = useState<PromptMode>('regular');
    const isInbound = mode === 'inbound';
    const regularVersions: PromptListe = usePromptsByAufgabeId(aufgabeId);
    const inboundVersions: PromptListe = usePromptsByAufgabeInbound(aufgabeId);
    const versions: PromptListe = isInbound ? inboundVersions : regularVersions;
    const activePromptId = isInbound ? aufgabe.active_inbound_prompt_id : aufgabe.activePromptId;
    const regularHandling = useAufgabenPromptHandling(aufgabeId);
    const inboundHandling = useAufgabenInboundPromptHandling(aufgabeId);
    const {createPromptMutation, setActivePromptMutation, deletePromptMutation} = isInbound ? inboundHandling : regularHandling;
    const canEdit = canEditAufgabeField(aufgabe.mandantId);
    const [selectedId, setSelectedId] = useState<string>(aufgabe.activePromptId);
    const [createOpen, setCreateOpen] = useState(false);

    const handleModeChange = (next: PromptMode) => {
        setMode(next);
        setSelectedId((next === 'inbound' ? aufgabe.active_inbound_prompt_id : aufgabe.activePromptId) ?? '');
    };

    return (
        <MultipleGuard
            permissions={[
                permissions.aufgaben.show,
                permissions.aufgaben.own_show,
            ]}
            fallback={<ErrorAlert error={notPermitted()}/>}
        >
            <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <PromptModeToggle mode={mode} onChange={handleModeChange}/>
                    <Button variant="contained" color="secondary" disabled={!canEdit} onClick={() => setCreateOpen(true)}>
                        {capitalize(translation('prompt-version-create'))}
                    </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{capitalize(translation('prompt-version-number'))}</TableCell>
                                <TableCell>{capitalize(translation('prompt-version-name'))}</TableCell>
                                <TableCell>{capitalize(translation('prompt-version-description'))}</TableCell>
                                <TableCell>{capitalize(translation('prompt-version-created'))}</TableCell>
                                <TableCell>{capitalize(translation('prompt-version-updated'))}</TableCell>
                                <TableCell>{capitalize(translation('prompt-version-status'))}</TableCell>
                                <TableCell align="right">{capitalize(translation('prompt-version-actions'))}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {versions.items.map((v: Prompt) => {
                                const isActive = v.id === activePromptId;
                                const isSelected = v.id === selectedId;
                                return (
                                    <TableRow
                                        key={v.id}
                                        hover
                                        selected={isSelected}
                                        onClick={() => setSelectedId(v.id)}
                                        sx={{cursor: 'pointer'}}
                                    >
                                        <TableCell>{versionLabel(versions.items, v.id)}</TableCell>
                                        <TableCell>{v.versionName}</TableCell>
                                        <TableCell>{v.versionDescription ?? ''}</TableCell>
                                        <TableCell>{v.created?.timestamp ? <LocalDate value={v.created.timestamp}/> : ''}</TableCell>
                                        <TableCell>{v.updated?.timestamp ? <LocalDate value={v.updated.timestamp}/> : ''}</TableCell>
                                        <TableCell>
                                            {isActive
                                                ? <Chip color="success" size="small" label={capitalize(translation('prompt-version-active'))}/>
                                                : <Chip variant="outlined" size="small" label={capitalize(translation('prompt-version-inactive'))}/>}
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title={capitalize(translation('prompt-version-edit'))}>
                                                <span>
                                                    <IconButton size="small" disabled={!canEdit} onClick={() => setSelectedId(v.id)}>
                                                        <EditOutlinedIcon fontSize="small"/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={capitalize(translation('prompt-version-activate'))}>
                                                <span>
                                                    <IconButton size="small" disabled={!canEdit || isActive || setActivePromptMutation.isLoading} onClick={() => setActivePromptMutation.mutate(v.id)}>
                                                        <CheckCircleIcon fontSize="small"/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={isActive ? capitalize(translation('cannot-delete-active-prompt')) : capitalize(translation('prompt-version-delete'))}>
                                                <span>
                                                    <IconButton size="small" color="error" disabled={!canEdit || isActive || deletePromptMutation.isLoading} onClick={() => deletePromptMutation.mutate(v.id)}>
                                                        <DeleteOutlineIcon fontSize="small"/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {isInbound ? (
                    <AufgabeInboundPromptVersionEditor
                        aufgabe={aufgabe}
                        canEdit={canEdit}
                        selectedPromptId={selectedId}
                        onSelectPrompt={setSelectedId}
                    />
                ) : (
                    <AufgabePromptVersionEditor
                        aufgabe={aufgabe}
                        canEdit={canEdit}
                        selectedPromptId={selectedId}
                        onSelectPrompt={setSelectedId}
                    />
                )}
                <DialogCreatePromptVersion
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    entityId={aufgabeId}
                    sprache={aufgabe.sprache}
                    createMutation={createPromptMutation}
                    seedPrompt={versions.items.find((v) => v.id === selectedId)}
                    onCreated={(prompt) => setSelectedId(prompt.id)}
                />
            </Stack>
        </MultipleGuard>
    );
}
